export type ResponsiveModalPart = "header" | "bodyViewport" | "bodyContent" | "footer";

const OVERFLOW_LOCK_ATTRIBUTE = "data-overflow-lock";

export interface IResponsiveModalHeightAnimator {
    setPanel: (panel: HTMLElement | null) => void;
    registerPart: (part: ResponsiveModalPart, element: HTMLElement) => () => void;
    setOpen: (isOpen: boolean) => void;
    setAnimationEnabled: (isEnabled: boolean) => void;
}

interface IAnimatorOptions {
    durationMs: number;
    isAnimationEnabled?: boolean;
}

interface IBodyLayout {
    baseHeight: number;
    minimumHeight: number;
}

interface ISettledLayout extends IBodyLayout {
    panelHeight: number;
    panelWidth: number;
    viewportWidth: number;
    viewportHeight: number;
}

const HEIGHT_EPSILON_PX = 1;
const SIZE_EPSILON_PX = 0.5;
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const LAYOUT_PROPERTY_PATTERN =
    /^(height|width|(min|max)-(height|width|block-size|inline-size)|block-size|inline-size|padding(-.+)?|margin(-.+)?|border(-(top|right|bottom|left|block|inline)(-(start|end))?)?(-width)?|grid(-.+)?|gap|row-gap|column-gap|flex(-.+)?|font|font-size|line-height|letter-spacing|word-spacing|aspect-ratio)$/;

const toKebabCase = (property: string) => property.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);

const toPx = (value: number) => `${value}px`;

const parsePx = (value: string) => parseFloat(value) || 0;

const prefersReducedMotion = () => typeof window.matchMedia === "function" && Boolean(window.matchMedia(REDUCED_MOTION_QUERY)?.matches);

const getAnimatedProperties = (animation: Animation): string[] => {
    if ("transitionProperty" in animation) {
        return [(animation as CSSTransition).transitionProperty];
    }

    const effect = animation.effect;

    if (!effect || !("getKeyframes" in effect)) {
        return [];
    }

    return (effect as KeyframeEffect).getKeyframes().reduce<string[]>((properties, keyframe) => properties.concat(Object.keys(keyframe).map(toKebabCase)), []);
};

const isProgressingLayoutAnimation = (animation: Animation) => {
    const effect = animation.effect;

    if (animation.playState !== "running" || !effect) {
        return false;
    }

    const {localTime} = effect.getComputedTiming();
    const {delay = 0} = effect.getTiming();

    if (typeof localTime !== "number" || localTime <= delay) {
        return false;
    }

    return getAnimatedProperties(animation).some((property) => LAYOUT_PROPERTY_PATTERN.test(property));
};

export const createResponsiveModalHeightAnimator = (options: IAnimatorOptions): IResponsiveModalHeightAnimator => {
    const observedParts = new Set<HTMLElement>();
    let panel: HTMLElement | null = null;
    let overlay: HTMLElement | null = null;
    let bodyViewport: HTMLElement | null = null;
    let bodyContent: HTMLElement | null = null;
    let footer: HTMLElement | null = null;
    let observer: ResizeObserver | null = null;
    let isOpen = false;
    let isAnimationEnabled = options.isAnimationEnabled ?? true;
    let animation: Animation | null = null;
    let revealAnimation: Animation | null = null;
    let revealTarget: HTMLElement | null = null;
    let clampedHeight: number | null = null;
    let lockedViewport: HTMLElement | null = null;
    let lockedPanel: HTMLElement | null = null;
    let settledLayout: ISettledLayout | null = null;
    let isUpdateScheduled = false;

    const getAnimatedOffset = () => (animation && bodyContent ? parsePx(window.getComputedStyle(bodyContent).marginBottom) : 0);

    const getRevealOffset = () => (revealAnimation && revealTarget ? parsePx(window.getComputedStyle(revealTarget).marginBottom) : 0);

    const measureBody = (panelHeight: number): IBodyLayout => {
        if (!bodyViewport || !bodyContent) {
            return {baseHeight: panelHeight, minimumHeight: panelHeight};
        }

        const outsideBodyHeight = panelHeight - bodyViewport.getBoundingClientRect().height - getRevealOffset();

        return {
            baseHeight: outsideBodyHeight + bodyContent.getBoundingClientRect().height,
            minimumHeight: outsideBodyHeight + parsePx(window.getComputedStyle(bodyViewport).minHeight)
        };
    };

    const settle = (panelRect: DOMRect, panelHeight = panelRect.height, bodyLayout = measureBody(panelRect.height)) => {
        settledLayout = {
            ...bodyLayout,
            panelHeight,
            panelWidth: panelRect.width,
            viewportWidth: overlay ? overlay.clientWidth : 0,
            viewportHeight: overlay ? overlay.clientHeight : 0
        };
    };

    const getHeightOnScreen = (layout: ISettledLayout) => {
        if (!animation) {
            return layout.panelHeight;
        }

        return Math.min(clampedHeight ?? Infinity, Math.max(layout.minimumHeight, layout.baseHeight + getAnimatedOffset()) + getRevealOffset());
    };

    const cancelRevealAnimation = () => {
        if (!revealAnimation) {
            return;
        }

        const runningAnimation = revealAnimation;
        revealAnimation = null;
        revealTarget = null;
        runningAnimation.cancel();
    };

    const cancelAnimation = () => {
        cancelRevealAnimation();

        if (!animation) {
            return;
        }

        const runningAnimation = animation;
        animation = null;
        runningAnimation.onfinish = null;
        runningAnimation.cancel();
    };

    const releaseViewportLock = () => {
        if (lockedViewport) {
            lockedViewport.removeAttribute(OVERFLOW_LOCK_ATTRIBUTE);
            lockedViewport = null;
        }
    };

    const releasePanelLock = () => {
        if (lockedPanel) {
            lockedPanel.removeAttribute(OVERFLOW_LOCK_ATTRIBUTE);
            lockedPanel = null;
        }
    };

    const releaseOverflowLock = () => {
        releaseViewportLock();
        releasePanelLock();
    };

    const lockOverflow = (element: HTMLElement) => {
        const style = window.getComputedStyle(element);
        const scrollbarWidth = element.offsetWidth - element.clientWidth - parsePx(style.borderLeftWidth) - parsePx(style.borderRightWidth);

        element.setAttribute(OVERFLOW_LOCK_ATTRIBUTE, scrollbarWidth > SIZE_EPSILON_PX ? "scroll" : "hidden");
    };

    const snap = (element: HTMLElement) => {
        cancelAnimation();
        releaseOverflowLock();
        clampedHeight = null;
        settle(element.getBoundingClientRect());
    };

    const hasResized = (previousLayout: ISettledLayout, panelRect: DOMRect) =>
        Math.abs(previousLayout.panelWidth - panelRect.width) >= SIZE_EPSILON_PX ||
        (overlay !== null && (previousLayout.viewportWidth !== overlay.clientWidth || previousLayout.viewportHeight !== overlay.clientHeight));

    const hasProgressingContentAnimation = (element: HTMLElement) => {
        if (typeof element.getAnimations !== "function") {
            return false;
        }

        return element.getAnimations({subtree: true}).some((item) => item !== animation && item !== revealAnimation && isProgressingLayoutAnimation(item));
    };

    const animateHeight = (element: HTMLElement, previousLayout: ISettledLayout) => {
        if (!bodyViewport || !bodyContent || typeof bodyContent.animate !== "function") {
            snap(element);
            return;
        }

        const fromHeight = getHeightOnScreen(previousLayout);

        cancelAnimation();

        const naturalRect = element.getBoundingClientRect();
        const toHeight = naturalRect.height;

        if (Math.abs(toHeight - fromHeight) < HEIGHT_EPSILON_PX) {
            releaseOverflowLock();
            clampedHeight = null;
            settle(naturalRect);
            return;
        }

        if (!lockedViewport) {
            lockOverflow(bodyViewport);
            lockedViewport = bodyViewport;
        }

        const bodyLayout = measureBody(toHeight);
        const revealDistance = bodyLayout.minimumHeight - fromHeight;
        const timing: KeyframeAnimationOptions = {duration: options.durationMs, easing: "ease-out"};
        const heightAnimation = bodyContent.animate(
            [{marginBottom: toPx(fromHeight - bodyLayout.baseHeight)}, {marginBottom: toPx(toHeight - bodyLayout.baseHeight)}],
            timing
        );

        if (revealDistance > HEIGHT_EPSILON_PX && toHeight > fromHeight) {
            revealTarget = footer ?? bodyViewport;

            if (!lockedPanel) {
                lockOverflow(element);
                lockedPanel = element;
            }

            revealAnimation = revealTarget.animate(
                [
                    {offset: 0, marginBottom: toPx(-revealDistance)},
                    {offset: Math.min(1, revealDistance / (toHeight - fromHeight)), marginBottom: "0px"},
                    {offset: 1, marginBottom: "0px"}
                ],
                timing
            );
        } else {
            releasePanelLock();
        }

        heightAnimation.onfinish = () => {
            if (animation !== heightAnimation) {
                return;
            }

            animation = null;
            revealAnimation = null;
            revealTarget = null;
            clampedHeight = null;
            releaseOverflowLock();

            if (panel) {
                settle(panel.getBoundingClientRect());
            }
        };

        animation = heightAnimation;
        clampedHeight = Math.max(bodyLayout.minimumHeight, bodyLayout.baseHeight) - toHeight > SIZE_EPSILON_PX ? toHeight : null;
        settle(naturalRect, fromHeight, bodyLayout);
    };

    const retargetAnimationEnd = (bodyLayout: IBodyLayout) => {
        const effect = animation?.effect;

        if (!effect || !("setKeyframes" in effect)) {
            return;
        }

        const {progress} = effect.getComputedTiming();
        const currentOffset = getAnimatedOffset();
        const endOffset = Math.min(clampedHeight ?? Infinity, Math.max(bodyLayout.minimumHeight, bodyLayout.baseHeight)) - bodyLayout.baseHeight;
        const startOffset = typeof progress === "number" && progress < 1 ? (currentOffset - endOffset * progress) / (1 - progress) : endOffset;

        (effect as KeyframeEffect).setKeyframes([{marginBottom: toPx(startOffset)}, {marginBottom: toPx(endOffset)}]);
    };

    const followContent = (panelRect: DOMRect) => {
        const bodyLayout = measureBody(panelRect.height);

        if (animation) {
            retargetAnimationEnd(bodyLayout);
        }

        settle(panelRect, panelRect.height, bodyLayout);
    };

    const update = (hasPartChange: boolean) => {
        if (!panel || !isAnimationEnabled) {
            return;
        }

        const element = panel;
        const rect = element.getBoundingClientRect();
        const previousLayout = settledLayout;

        if (!previousLayout || hasResized(previousLayout, rect) || !isOpen || prefersReducedMotion()) {
            snap(element);
            return;
        }

        if (!hasPartChange) {
            return;
        }

        if (hasProgressingContentAnimation(element)) {
            followContent(rect);
            return;
        }

        animateHeight(element, previousLayout);
    };

    const getObserver = () => {
        if (!observer && typeof ResizeObserver !== "undefined") {
            observer = new ResizeObserver((entries) => update(entries.some((entry) => entry.target !== overlay)));
        }

        return observer;
    };

    const observe = (element: HTMLElement) => {
        if (!isAnimationEnabled) {
            return;
        }

        getObserver()?.observe(element, {box: "border-box"});
    };

    const observeAll = () => {
        if (overlay) {
            observe(overlay);
        }

        observedParts.forEach(observe);
    };

    const stopObserving = () => {
        observer?.disconnect();
        observer = null;
    };

    const reset = () => {
        cancelAnimation();
        releaseOverflowLock();
        clampedHeight = null;
        settledLayout = null;
    };

    const scheduleUpdate = () => {
        if (isUpdateScheduled || typeof queueMicrotask !== "function") {
            return;
        }

        isUpdateScheduled = true;
        queueMicrotask(() => {
            isUpdateScheduled = false;
            update(true);
        });
    };

    const setPanel = (element: HTMLElement | null) => {
        if (element === panel) {
            return;
        }

        reset();

        if (overlay) {
            observer?.unobserve(overlay);
        }

        panel = element;
        overlay = element ? element.parentElement : null;

        if (!element) {
            stopObserving();
            return;
        }

        observeAll();
    };

    const registerPart = (part: ResponsiveModalPart, element: HTMLElement) => {
        if (part === "bodyViewport") {
            bodyViewport = element;

            return () => {
                if (bodyViewport === element) {
                    releaseViewportLock();
                    bodyViewport = null;
                }
            };
        }

        if (part === "bodyContent") {
            bodyContent = element;
        }

        if (part === "footer") {
            footer = element;
        }

        observedParts.add(element);
        observe(element);

        return () => {
            observedParts.delete(element);
            observer?.unobserve(element);

            if (bodyContent === element) {
                if (animation && settledLayout) {
                    settledLayout = {...settledLayout, panelHeight: getHeightOnScreen(settledLayout)};
                }

                cancelAnimation();
                bodyContent = null;
            }

            if (footer === element) {
                cancelRevealAnimation();
                footer = null;
            }

            scheduleUpdate();
        };
    };

    const setOpen = (value: boolean) => {
        isOpen = value;
    };

    const setAnimationEnabled = (value: boolean) => {
        if (value === isAnimationEnabled) {
            return;
        }

        isAnimationEnabled = value;

        if (!value) {
            reset();
            stopObserving();
            return;
        }

        observeAll();
    };

    return {setPanel, registerPart, setOpen, setAnimationEnabled};
};
export type ResponsiveModalPart = "header" | "bodyViewport" | "bodyContent" | "footer";

const OVERFLOW_LOCK_ATTRIBUTE = "data-overflow-lock";

export interface IResponsiveModalHeightAnimator {
    setPanel: (panel: HTMLElement | null) => void;
    registerPart: (part: ResponsiveModalPart, element: HTMLElement) => () => void;
    setOpen: (isOpen: boolean) => void;
    setAnimationEnabled: (isEnabled: boolean) => void;
}

interface IAnimatorOptions {
    durationMs: number;
    isAnimationEnabled?: boolean;
}

interface IBodyLayout {
    baseHeight: number;
    minimumHeight: number;
}

interface ISettledLayout extends IBodyLayout {
    panelHeight: number;
    panelWidth: number;
    viewportWidth: number;
    viewportHeight: number;
}

const HEIGHT_EPSILON_PX = 1;
const SIZE_EPSILON_PX = 0.5;
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const LAYOUT_PROPERTY_PATTERN =
    /^(height|width|(min|max)-(height|width|block-size|inline-size)|block-size|inline-size|padding(-.+)?|margin(-.+)?|border(-(top|right|bottom|left|block|inline)(-(start|end))?)?(-width)?|grid(-.+)?|gap|row-gap|column-gap|flex(-.+)?|font|font-size|line-height|letter-spacing|word-spacing|aspect-ratio)$/;

const toKebabCase = (property: string) => property.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);

const toPx = (value: number) => `${value}px`;

const parsePx = (value: string) => parseFloat(value) || 0;

const prefersReducedMotion = () => typeof window.matchMedia === "function" && Boolean(window.matchMedia(REDUCED_MOTION_QUERY)?.matches);

const getAnimatedProperties = (animation: Animation): string[] => {
    if ("transitionProperty" in animation) {
        return [(animation as CSSTransition).transitionProperty];
    }

    const effect = animation.effect;

    if (!effect || !("getKeyframes" in effect)) {
        return [];
    }

    return (effect as KeyframeEffect).getKeyframes().reduce<string[]>((properties, keyframe) => properties.concat(Object.keys(keyframe).map(toKebabCase)), []);
};

const isProgressingLayoutAnimation = (animation: Animation) => {
    const effect = animation.effect;

    if (animation.playState !== "running" || !effect) {
        return false;
    }

    const {localTime} = effect.getComputedTiming();
    const {delay = 0} = effect.getTiming();

    if (typeof localTime !== "number" || localTime <= delay) {
        return false;
    }

    return getAnimatedProperties(animation).some((property) => LAYOUT_PROPERTY_PATTERN.test(property));
};

export const createResponsiveModalHeightAnimator = (options: IAnimatorOptions): IResponsiveModalHeightAnimator => {
    const observedParts = new Set<HTMLElement>();
    let panel: HTMLElement | null = null;
    let overlay: HTMLElement | null = null;
    let bodyViewport: HTMLElement | null = null;
    let bodyContent: HTMLElement | null = null;
    let footer: HTMLElement | null = null;
    let observer: ResizeObserver | null = null;
    let isOpen = false;
    let isAnimationEnabled = options.isAnimationEnabled ?? true;
    let animation: Animation | null = null;
    let revealAnimation: Animation | null = null;
    let revealTarget: HTMLElement | null = null;
    let clampedHeight: number | null = null;
    let lockedViewport: HTMLElement | null = null;
    let lockedPanel: HTMLElement | null = null;
    let settledLayout: ISettledLayout | null = null;
    let isUpdateScheduled = false;

    const getAnimatedOffset = () => (animation && bodyContent ? parsePx(window.getComputedStyle(bodyContent).marginBottom) : 0);

    const getRevealOffset = () => (revealAnimation && revealTarget ? parsePx(window.getComputedStyle(revealTarget).marginBottom) : 0);

    const measureBody = (panelHeight: number): IBodyLayout => {
        if (!bodyViewport || !bodyContent) {
            return {baseHeight: panelHeight, minimumHeight: panelHeight};
        }

        const outsideBodyHeight = panelHeight - bodyViewport.getBoundingClientRect().height - getRevealOffset();

        return {
            baseHeight: outsideBodyHeight + bodyContent.getBoundingClientRect().height,
            minimumHeight: outsideBodyHeight + parsePx(window.getComputedStyle(bodyViewport).minHeight)
        };
    };

    const settle = (panelRect: DOMRect, panelHeight = panelRect.height, bodyLayout = measureBody(panelRect.height)) => {
        settledLayout = {
            ...bodyLayout,
            panelHeight,
            panelWidth: panelRect.width,
            viewportWidth: overlay ? overlay.clientWidth : 0,
            viewportHeight: overlay ? overlay.clientHeight : 0
        };
    };

    const getHeightOnScreen = (layout: ISettledLayout) => {
        if (!animation) {
            return layout.panelHeight;
        }

        return Math.min(clampedHeight ?? Infinity, Math.max(layout.minimumHeight, layout.baseHeight + getAnimatedOffset()) + getRevealOffset());
    };

    const cancelRevealAnimation = () => {
        if (!revealAnimation) {
            return;
        }

        const runningAnimation = revealAnimation;
        revealAnimation = null;
        revealTarget = null;
        runningAnimation.cancel();
    };

    const cancelAnimation = () => {
        cancelRevealAnimation();

        if (!animation) {
            return;
        }

        const runningAnimation = animation;
        animation = null;
        runningAnimation.onfinish = null;
        runningAnimation.cancel();
    };

    const releaseViewportLock = () => {
        if (lockedViewport) {
            lockedViewport.removeAttribute(OVERFLOW_LOCK_ATTRIBUTE);
            lockedViewport = null;
        }
    };

    const releasePanelLock = () => {
        if (lockedPanel) {
            lockedPanel.removeAttribute(OVERFLOW_LOCK_ATTRIBUTE);
            lockedPanel = null;
        }
    };

    const releaseOverflowLock = () => {
        releaseViewportLock();
        releasePanelLock();
    };

    const lockOverflow = (element: HTMLElement) => {
        const style = window.getComputedStyle(element);
        const scrollbarWidth = element.offsetWidth - element.clientWidth - parsePx(style.borderLeftWidth) - parsePx(style.borderRightWidth);

        element.setAttribute(OVERFLOW_LOCK_ATTRIBUTE, scrollbarWidth > SIZE_EPSILON_PX ? "scroll" : "hidden");
    };

    const snap = (element: HTMLElement) => {
        cancelAnimation();
        releaseOverflowLock();
        clampedHeight = null;
        settle(element.getBoundingClientRect());
    };

    const hasResized = (previousLayout: ISettledLayout, panelRect: DOMRect) =>
        Math.abs(previousLayout.panelWidth - panelRect.width) >= SIZE_EPSILON_PX ||
        (overlay !== null && (previousLayout.viewportWidth !== overlay.clientWidth || previousLayout.viewportHeight !== overlay.clientHeight));

    const hasProgressingContentAnimation = (element: HTMLElement) => {
        if (typeof element.getAnimations !== "function") {
            return false;
        }

        return element.getAnimations({subtree: true}).some((item) => item !== animation && item !== revealAnimation && isProgressingLayoutAnimation(item));
    };

    const animateHeight = (element: HTMLElement, previousLayout: ISettledLayout) => {
        if (!bodyViewport || !bodyContent || typeof bodyContent.animate !== "function") {
            snap(element);
            return;
        }

        const fromHeight = getHeightOnScreen(previousLayout);

        cancelAnimation();

        const naturalRect = element.getBoundingClientRect();
        const toHeight = naturalRect.height;

        if (Math.abs(toHeight - fromHeight) < HEIGHT_EPSILON_PX) {
            releaseOverflowLock();
            clampedHeight = null;
            settle(naturalRect);
            return;
        }

        if (!lockedViewport) {
            lockOverflow(bodyViewport);
            lockedViewport = bodyViewport;
        }

        const bodyLayout = measureBody(toHeight);
        const revealDistance = bodyLayout.minimumHeight - fromHeight;
        const timing: KeyframeAnimationOptions = {duration: options.durationMs, easing: "ease-out"};
        const heightAnimation = bodyContent.animate(
            [{marginBottom: toPx(fromHeight - bodyLayout.baseHeight)}, {marginBottom: toPx(toHeight - bodyLayout.baseHeight)}],
            timing
        );

        if (revealDistance > HEIGHT_EPSILON_PX && toHeight > fromHeight) {
            revealTarget = footer ?? bodyViewport;

            if (!lockedPanel) {
                lockOverflow(element);
                lockedPanel = element;
            }

            revealAnimation = revealTarget.animate(
                [
                    {offset: 0, marginBottom: toPx(-revealDistance)},
                    {offset: Math.min(1, revealDistance / (toHeight - fromHeight)), marginBottom: "0px"},
                    {offset: 1, marginBottom: "0px"}
                ],
                timing
            );
        } else {
            releasePanelLock();
        }

        heightAnimation.onfinish = () => {
            if (animation !== heightAnimation) {
                return;
            }

            animation = null;
            revealAnimation = null;
            revealTarget = null;
            clampedHeight = null;
            releaseOverflowLock();

            if (panel) {
                settle(panel.getBoundingClientRect());
            }
        };

        animation = heightAnimation;
        clampedHeight = Math.max(bodyLayout.minimumHeight, bodyLayout.baseHeight) - toHeight > SIZE_EPSILON_PX ? toHeight : null;
        settle(naturalRect, fromHeight, bodyLayout);
    };

    const retargetAnimationEnd = (bodyLayout: IBodyLayout) => {
        const effect = animation?.effect;

        if (!effect || !("setKeyframes" in effect)) {
            return;
        }

        const {progress} = effect.getComputedTiming();
        const currentOffset = getAnimatedOffset();
        const endOffset = Math.min(clampedHeight ?? Infinity, Math.max(bodyLayout.minimumHeight, bodyLayout.baseHeight)) - bodyLayout.baseHeight;
        const startOffset = typeof progress === "number" && progress < 1 ? (currentOffset - endOffset * progress) / (1 - progress) : endOffset;

        (effect as KeyframeEffect).setKeyframes([{marginBottom: toPx(startOffset)}, {marginBottom: toPx(endOffset)}]);
    };

    const followContent = (panelRect: DOMRect) => {
        const bodyLayout = measureBody(panelRect.height);

        if (animation) {
            retargetAnimationEnd(bodyLayout);
        }

        settle(panelRect, panelRect.height, bodyLayout);
    };

    const update = (hasPartChange: boolean) => {
        if (!panel || !isAnimationEnabled) {
            return;
        }

        const element = panel;
        const rect = element.getBoundingClientRect();
        const previousLayout = settledLayout;

        if (!previousLayout || hasResized(previousLayout, rect) || !isOpen || prefersReducedMotion()) {
            snap(element);
            return;
        }

        if (!hasPartChange) {
            return;
        }

        if (hasProgressingContentAnimation(element)) {
            followContent(rect);
            return;
        }

        animateHeight(element, previousLayout);
    };

    const getObserver = () => {
        if (!observer && typeof ResizeObserver !== "undefined") {
            observer = new ResizeObserver((entries) => update(entries.some((entry) => entry.target !== overlay)));
        }

        return observer;
    };

    const observe = (element: HTMLElement) => {
        if (!isAnimationEnabled) {
            return;
        }

        getObserver()?.observe(element, {box: "border-box"});
    };

    const observeAll = () => {
        if (overlay) {
            observe(overlay);
        }

        observedParts.forEach(observe);
    };

    const stopObserving = () => {
        observer?.disconnect();
        observer = null;
    };

    const reset = () => {
        cancelAnimation();
        releaseOverflowLock();
        clampedHeight = null;
        settledLayout = null;
    };

    const scheduleUpdate = () => {
        if (isUpdateScheduled || typeof queueMicrotask !== "function") {
            return;
        }

        isUpdateScheduled = true;
        queueMicrotask(() => {
            isUpdateScheduled = false;
            update(true);
        });
    };

    const setPanel = (element: HTMLElement | null) => {
        if (element === panel) {
            return;
        }

        reset();

        if (overlay) {
            observer?.unobserve(overlay);
        }

        panel = element;
        overlay = element ? element.parentElement : null;

        if (!element) {
            stopObserving();
            return;
        }

        observeAll();
    };

    const registerPart = (part: ResponsiveModalPart, element: HTMLElement) => {
        if (part === "bodyViewport") {
            bodyViewport = element;

            return () => {
                if (bodyViewport === element) {
                    releaseViewportLock();
                    bodyViewport = null;
                }
            };
        }

        if (part === "bodyContent") {
            bodyContent = element;
        }

        if (part === "footer") {
            footer = element;
        }

        observedParts.add(element);
        observe(element);

        return () => {
            observedParts.delete(element);
            observer?.unobserve(element);

            if (bodyContent === element) {
                if (animation && settledLayout) {
                    settledLayout = {...settledLayout, panelHeight: getHeightOnScreen(settledLayout)};
                }

                cancelAnimation();
                bodyContent = null;
            }

            if (footer === element) {
                cancelRevealAnimation();
                footer = null;
            }

            scheduleUpdate();
        };
    };

    const setOpen = (value: boolean) => {
        isOpen = value;
    };

    const setAnimationEnabled = (value: boolean) => {
        if (value === isAnimationEnabled) {
            return;
        }

        isAnimationEnabled = value;

        if (!value) {
            reset();
            stopObserving();
            return;
        }

        observeAll();
    };

    return {setPanel, registerPart, setOpen, setAnimationEnabled};
};
